import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import type { CurrencyFormats } from '@velasca/types'
import { formatMoney } from '@velasca/utils/format-money'

const ExchangeRatesContext = createContext<{
  setCurrency: React.Dispatch<React.SetStateAction<string | null>>
  convert: (priceRaw: number) => string
  currency: any
}>({
  setCurrency: () => {},
  convert: (priceRaw) => `${priceRaw}`,
  currency: '',
})

export const useExchangeRates = () => useContext(ExchangeRatesContext)

// TODO: replace with useLocalStorage
const userSelectedCurrency = localStorage.getItem('currency')

interface ExchangeRatesProviderProps {
  children: React.ReactNode
  currencyFormat: CurrencyFormats
  defaultFormat: string
  defaultCurrency: string
  active?: boolean
}

const ExchangeRatesProvider = ({
  children,
  currencyFormat,
  defaultFormat,
  defaultCurrency,
  active = false,
}: ExchangeRatesProviderProps) => {
  // TODO: replace with useLocalStorage
  const [currency, setCurrency] = useState(
    active ? userSelectedCurrency : defaultCurrency,
  )

  useEffect(() => {
    if (currency && active) {
      // TODO: replace with useLocalStorage
      localStorage.setItem('currency', currency)
    }
  }, [currency, active])

  const selectedCurrency = useMemo(() => {
    if (defaultCurrency) {
      if (currency) {
        return currency
      }

      return defaultCurrency
    }

    return null
  }, [currency, defaultCurrency])

  // TODO: always pass centesimal pricing ($10 = 1000) as number. In this way we can abstract from actual money_format
  // The reason this was done this way it's because the original code was reading the price from HTML printed by liquid
  // so it *needed* the format. Using React, the format is not needed anymore because we can convert *before* we print
  const convert = useCallback(
    (priceRaw: number) => {
      if (!active || !defaultCurrency || !selectedCurrency) {
        return formatMoney(priceRaw, defaultFormat)
      }

      const selectedCurrencyFormat =
        window.Currency.moneyFormats[selectedCurrency][currencyFormat] ||
        '{{amount}}'

      const cents = window.Currency.convert(
        priceRaw,
        defaultCurrency,
        selectedCurrency,
      )

      return formatMoney(cents, selectedCurrencyFormat)
    },
    [active, defaultCurrency, selectedCurrency, currencyFormat, defaultFormat],
  )

  return (
    <ExchangeRatesContext.Provider
      value={{
        setCurrency,
        convert,
        currency: selectedCurrency,
      }}
    >
      {children}
    </ExchangeRatesContext.Provider>
  )
}

export default ExchangeRatesProvider
